import { json, LoaderFunction, redirect } from "@remix-run/node";
import { useLoaderData, NavLink, useNavigate } from "@remix-run/react";
import { MetaFunction, LinksFunction } from "@vercel/remix";
import { useEffect } from "react";
import { BarChart, Check, Cloud, RefreshCw, Shield } from "lucide-react";
import { SignUpButton, useAuth } from "@clerk/remix";
import Footer from "~/components/Footer";
import { getAuth } from "@clerk/remix/ssr.server";
import {
  generateStructuredData,
  generateMetaData,
  PRODUCT_FEATURES,
} from "~/utils/structuredData";
import StructuredData from "~/components/StructuredData";
import { useLottie } from "~/hooks/useLottie";
import SampleQuestions from "~/components/SampleQuestions";
import BlackFridayBanner from "~/components/BlackFridayBanner";

export const links: LinksFunction = () => [
  { rel: "canonical", href: "https://cloudexamhub.com/" },
];

export const meta: MetaFunction = () => {
  const metaData = generateMetaData();

  return [
    { title: metaData.title },
    { name: "description", content: metaData.description },
    { name: "keywords", content: metaData.keywords },
    { property: "og:title", content: metaData["og:title"] },
    { property: "og:description", content: metaData["og:description"] },
    { property: "og:type", content: metaData["og:type"] },
    { property: "og:image", content: metaData["og:image"] },
    { property: "og:url", content: metaData["og:url"] },
    { name: "twitter:card", content: metaData["twitter:card"] },
    { name: "twitter:title", content: metaData["twitter:title"] },
    { name: "twitter:description", content: metaData["twitter:description"] },
    { name: "twitter:image", content: metaData["twitter:image"] },
  ];
};

export const loader: LoaderFunction = async (args) => {
  console.log("Index loader function called");
  const { userId } = await getAuth(args);

  if (userId) {
    console.log("Redirecting to /user-home from index");
    return redirect("/user-home");
  }

  return json({ userId });
};

export default function LandingPage() {
  const { userId } = useLoaderData<typeof loader>();
  const { isLoaded, userId: clientUserId } = useAuth();
  const navigate = useNavigate();
  const structuredData = generateStructuredData();

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "How up-to-date are your AWS Cloud Practitioner practice questions?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Our question bank matches the latest CLF-C02 exam objectives and AWS services. Plus, we continuously add questions so by signing up, you benefit from a growing library of practice questions.",
        },
      },
      {
        "@type": "Question",
        name: "How do your practice tests compare to the real CLF-C02 exam?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Our practice tests accurately simulate the real exam experience with the same number of questions, format of questions, and difficulty level. Questions cover all official exam domains in similar proportions.",
        },
      },
      {
        "@type": "Question",
        name: "What if I'm not satisfied?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "While our 500+ practice questions and detailed explanations give you the best preparation possible, we offer a 14-day money-back guarantee if you're not completely satisfied with our platform.",
        },
      },
      {
        "@type": "Question",
        name: "How long do I have access to the practice tests?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Your purchase includes unlimited access to all 500+ questions, practice tests, and performance analytics. Study at your own pace and take as many practice exams as you need. No subscription required.",
        },
      },
    ],
  };

  useEffect(() => {
    if (isLoaded && (clientUserId || userId)) {
      console.log("Client-side redirect to /user-home");
      navigate("/user-home");
    }
  }, [isLoaded, clientUserId, userId, navigate]);

  return (
    <div className="flex flex-col min-h-screen mx-auto">
      <StructuredData data={structuredData} />
      <StructuredData data={faqSchema} />

      <BlackFridayBanner />

      <header className="sticky top-0 z-50 bg-white border-b px-4 sm:px-8 lg:px-12 h-16 sm:h-14 flex items-center">
        <NavLink to="/" className="flex items-center justify-center">
          <img
            src="/cloud-check.png"
            alt="Cloud Exam Hub"
            className="h-8 w-8 sm:h-6 sm:w-6"
          />
          <span className="ml-2 font-bold text-lg sm:text-xl truncate">
            Cloud Exam Hub
          </span>
        </NavLink>
        <nav className="ml-auto flex gap-4 sm:gap-4 items-center">
          <NavLink
            to="#sample-questions"
            className="text-sm font-medium hover:underline underline-offset-4 hidden sm:inline"
          >
            Free Sample
          </NavLink>
          <NavLink
            to="#features"
            className="text-sm font-medium hover:underline underline-offset-4 hidden sm:inline"
          >
            Features
          </NavLink>
          <NavLink
            to="#pricing"
            className="text-sm font-medium hover:underline underline-offset-4 hidden sm:inline"
          >
            Pricing
          </NavLink>
          <NavLink
            to="#faq"
            className="text-sm font-medium hover:underline underline-offset-4 hidden sm:inline"
          >
            FAQ
          </NavLink>

          <SignUpButton mode="modal">
            <button className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-md transition-colors">
              Get Started
            </button>
          </SignUpButton>
        </nav>
      </header>
      <main className="flex-1">
        <section className="w-full py-20 md:py-28 lg:py-32 bg-gradient-to-r from-blue-600 via-blue-500 to-cyan-400">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <div className="flex flex-col lg:flex-row items-center lg:justify-between">
              <div className="flex flex-col justify-center space-y-6 text-white lg:w-1/2">
                <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl">
                  Pass the AWS Cloud Practitioner Exam with Confidence
                </h1>
                <p className="max-w-[600px] text-gray-200 text-lg md:text-xl lg:text-2xl">
                  Master the AWS CLF-C02 exam with 500+ up-to-date practice
                  questions, detailed explanations, and unlimited test
                  simulations.
                </p>
                <div className="flex flex-col gap-3 items-start">
                  <SignUpButton mode="modal">
                    <button className="inline-flex items-center justify-center rounded-md text-xl font-bold bg-white text-blue-600 hover:bg-gray-100 h-16 px-10 py-4 transition-colors">
                      Start Practicing Now
                    </button>
                  </SignUpButton>
                  <p className="text-sm text-blue-100 flex items-center gap-1">
                    <Shield className="h-4 w-4 flex-shrink-0" />
                    $11.99 one-time · 14-day money-back guarantee · No subscription
                  </p>
                </div>
              </div>
              <div className="flex justify-center items-center lg:w-1/2 mt-12 lg:mt-0">
                <div
                  ref={useLottie("/test-Animation.json")}
                  className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl"
                />
              </div>
            </div>
          </div>
        </section>
        <section
          className="w-full py-12 md:py-24 lg:py-32 bg-gray-50"
          id="sample-questions"
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl text-center mb-12">
              Try Some Sample Questions
            </h2>
            <SampleQuestions />
            <div className="mt-12 text-center">
              <p className="text-gray-600 mb-4 text-lg">Like what you see? Get access to 500+ questions just like these.</p>
              <SignUpButton mode="modal">
                <button className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-3 rounded-lg text-lg transition-colors">
                  Get Full Access for $11.99 →
                </button>
              </SignUpButton>
              <p className="mt-2 text-sm text-gray-500">14-day money-back guarantee</p>
            </div>
          </div>
        </section>
        <section className="w-full py-12 md:py-24 lg:py-32" id="features">
          <div className="container mx-auto px-6 sm:px-8 lg:px-12 max-w-7xl">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl text-center mb-12">
              The Fastest Path to AWS Certification
            </h2>
            <div className="grid gap-6 lg:grid-cols-3 lg:gap-12">
              <div className="flex flex-col items-center space-y-4 text-center">
                <Cloud className="h-12 w-12 text-blue-500" />
                <h3 className="text-xl font-bold">500+ Practice Questions</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Comprehensive question bank covering all CLF-C02 exam domains,
                  with detailed explanations for every question. We specialize
                  in the CLF-C02 exam.
                </p>
              </div>
              <div className="flex flex-col items-center space-y-4 text-center">
                <RefreshCw className="h-12 w-12 text-blue-500" />
                <h3 className="text-xl font-bold">Learn From Your Mistakes</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Each answer includes detailed explanations and analogies to
                  help you understand the concepts. Understand not just
                  what&apos;s correct, but why. Take unlimited practice exams
                  until you&apos;re ready.
                </p>
              </div>
              <div className="flex flex-col items-center space-y-4 text-center">
                <BarChart className="h-12 w-12 text-blue-500" />
                <h3 className="text-xl font-bold">Track Your Readiness</h3>
                <p className="text-gray-500 dark:text-gray-400">
                  Our analytics track your progress so you know when you&apos;re
                  ready to take the real exam. Focus your study time on weak
                  areas with domain-specific scoring.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section
          className="w-full py-12 md:py-24 lg:py-32 bg-gray-100"
          id="pricing"
        >
          <div className="container mx-auto px-6 sm:px-8 lg:px-12 max-w-7xl">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl text-center mb-12">
              Special Pricing (Limited Time)
            </h2>
            <div className="flex justify-center">
              <div className="flex flex-col p-8 bg-blue-100 rounded-lg shadow-lg max-w-md w-full">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-blue-900 bg-white px-3 py-1 rounded-full">
                    Limited-Time Deal
                  </span>
                </div>
                <h3 className="text-2xl font-bold text-center mb-4 text-gray-800">
                  Unlimited Access
                </h3>
                <div className="text-center mb-6">
                  <span className="text-lg line-through text-gray-700">
                    $19.99
                  </span>
                  <p className="text-4xl font-bold text-blue-600">$11.99</p>
                  <p className="text-lg text-black-600 mt-2 font-bold">
                    Save 40% — limited-time offer
                  </p>
                </div>
                <ul className="space-y-2 mb-6">
                  {PRODUCT_FEATURES.map((feature) => (
                    <li
                      key={feature}
                      className="flex items-center text-gray-600"
                    >
                      <Check className="w-5 h-5 mr-2 text-green-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <SignUpButton mode="modal">
                  <button className="w-full bg-blue-600 text-white hover:bg-blue-700 rounded-md py-3 font-bold text-lg">
                    Get Instant Access
                  </button>
                </SignUpButton>
                <div className="flex items-center justify-center gap-1 mt-3 text-sm text-gray-500">
                  <Shield className="h-4 w-4 text-green-500 flex-shrink-0" />
                  14-day money-back guarantee, no questions asked
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="w-full py-12 md:py-24 lg:py-32" id="faq">
          <div className="container mx-auto px-6 sm:px-8 lg:px-12 max-w-7xl">
            <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl text-center mb-12 text-gray-800">
              Frequently Asked Questions
            </h2>
            <div className="grid gap-6 lg:grid-cols-2 lg:gap-12">
              {[
                {
                  q: "How up-to-date are your AWS Cloud Practitioner practice questions?",
                  a: "Our question bank matches the latest CLF-C02 exam objectives and AWS services. Plus, we continuously add questions so by signing up, you benefit from a growing library of practice questions.",
                },
                {
                  q: "How do your practice tests compare to the real CLF-C02 exam?",
                  a: "Our practice tests accurately simulate the real exam experience with the same number of questions, format of questions, and difficulty level. Questions cover all official exam domains in similar proportions.",
                },
                {
                  q: "What if I'm not satisfied?",
                  a: "While our 500+ practice questions and detailed explanations give you the best preparation possible, we offer a 14-day money-back guarantee if you're not completely satisfied with our platform.",
                },
                {
                  q: "How long do I have access to the practice tests?",
                  a: "Your purchase includes unlimited access to all 500+ questions, practice tests, and performance analytics. Study at your own pace and take as many practice exams as you need. No annoying subscription required.",
                },
              ].map((item) => (
                <div
                  key={item.q}
                  className="space-y-2 bg-white p-6 rounded-lg shadow-md"
                >
                  <h3 className="text-xl font-bold text-gray-800">{item.q}</h3>
                  <p className="text-gray-600">{item.a}</p>
                </div>
              ))}
            </div>
            <p className="text-center mt-8 text-gray-600">
              Have more questions? Check out our detailed{" "}
              <NavLink
                to="/certification-faqs"
                className="text-blue-600 hover:text-blue-700 hover:underline"
              >
                AWS Cloud Practitioner FAQ Guide
              </NavLink>{" "}
              or review our{" "}
              <NavLink
                to="/checklist"
                className="text-blue-600 hover:text-blue-700 hover:underline"
              >
                exam preparation checklist
              </NavLink>{" "}
              and{" "}
              <NavLink
                to="/glossary"
                className="text-blue-600 hover:text-blue-700 hover:underline"
              >
                AWS terminology glossary
              </NavLink>
              .
            </p>
          </div>
          <div className="flex justify-center mt-12">
            <SignUpButton mode="modal">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg text-xl font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors h-16 px-8 py-4 shadow-md hover:shadow-lg"
              >
                Try Some Practice Questions
              </button>
            </SignUpButton>
          </div>
        </section>
        <section className="w-full py-12 md:py-24 lg:py-32 bg-blue-600">
          <div className="container mx-auto px-6 sm:px-8 lg:px-12 max-w-7xl">
            <div className="flex flex-col items-center space-y-4 text-center text-white">
              <h2 className="text-3xl font-bold tracking-tighter sm:text-5xl">
                Ready to Pass Your AWS Cloud Practitioner Exam?
              </h2>
              <p className="max-w-[600px] text-gray-200 md:text-xl">
                Don&apos;t waste time with outdated study guides. Get instant
                access to 500+ practice questions and pass your AWS CLF-C02 exam
                on the first try. Special discounted pricing ends soon!
              </p>
              <SignUpButton mode="modal">
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-md text-xl font-bold bg-white text-blue-600 hover:bg-gray-100 h-16 px-8 py-4 transition-colors"
                >
                  Get Instant Access for $11.99
                </button>
              </SignUpButton>
              <p className="text-blue-200 text-sm">One-time payment · No subscription · 14-day money-back guarantee</p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
